import { useEffect, useState } from "react";
import Auth from "src/Auth";
import { useSudowriteStore } from "src/stores/sudowriteStore";
import { replacementSampleState, useBenchmarkTakeoverStore } from "src/stores/benchmarkTakeoverStore";
import { createSafeStorage } from "src/stores/safeStorage";

const VERSION = 1;
const dismissedKey = (userId, documentId) => `comparison-dismissed:${encodeURIComponent(userId)}:${encodeURIComponent(documentId)}`;
const draftKey = (userId, documentId) => `comparison-draft:${encodeURIComponent(userId)}:${encodeURIComponent(documentId)}`;
// Save completed reviews and replacement checkpoints, including the abandoned
// attempt's feedback. Streaming handles and insertion side effects never resume.
const FIELDS = ["sessionId", "surface", "precedingText", "stage", "samples", "sampleDone", "sampleRevisions", "previousSampleDrafts", "generationDone", "scores", "aiIsms", "annotations", "choice", "decisionReason", "readMs", "copyAttempts", "feedbackPasteEvents", "focusBlurCount", "revealedAt", "failure", "ratingClosedMessage"];

function storage() {
  try {
    return createSafeStorage(window.sessionStorage);
  } catch {
    return createSafeStorage(null);
  }
}

export function readComparisonDraft(storage, key) {
  try {
    const saved = JSON.parse(storage.getItem(key));
    const draft = saved?.draft;
    const replacing = ["A", "B"].some((sample) => draft?.sampleRevisions?.[sample] > 0);
    const validStage = ["loading", "A", "B", "compare"].includes(draft?.stage) || (draft?.stage === "closed" && draft.failure?.preserveSamples);
    if (saved?.version !== VERSION || !draft?.sessionId || (!draft.generationDone && !replacing) || !validStage ||
      typeof draft.samples?.A?.text !== "string" || typeof draft.samples?.B?.text !== "string" || !draft.scores?.A || !draft.scores?.B || !Array.isArray(draft.annotations)) return null;
    return {
      ...Object.fromEntries(FIELDS.map((field) => [field, draft[field]])),
      sampleRevisions: { A: 0, B: 0, ...draft.sampleRevisions },
      previousSampleDrafts: { A: null, B: null, ...draft.previousSampleDrafts },
      ...(!draft.generationDone ? { generationDone: true, stage: draft.stage === "closed" ? "closed" : "compare", failure: { message: "This comparison was interrupted. Your feedback is saved here.", preserveSamples: true, historySaved: null } } : {}),
    };
  } catch {
    return null;
  }
}

function isDismissed(userId, documentId, sessionId) {
  try { return JSON.parse(storage().getItem(dismissedKey(userId, documentId)) || "[]").includes(sessionId); } catch { return false; }
}

function rememberDismissal(userId, documentId, sessionId) {
  try {
    const key = dismissedKey(userId, documentId);
    const dismissals = storage();
    const previous = JSON.parse(dismissals.getItem(key) || "[]");
    dismissals.setItem(key, JSON.stringify([sessionId, ...previous.filter((id) => id !== sessionId)].slice(0, 30)));
  } catch { /* Malformed saved dismissals must not stop closing a comparison. */ }
}

export function useComparisonDraftRecovery({ userId, documentId }) {
  const [recoverable, setRecoverable] = useState(null);
  const hasAccess = useSudowriteStore((state) => !!state.subscription?.hasAccessToMuse2AlphaPhase2);
  const failedSessionId = useBenchmarkTakeoverStore((state) => state.failure && !state.ratingClosedMessage ? state.sessionId : null);
  useEffect(() => {
    if (!userId || !documentId) return undefined;
    const draftStorage = storage();
    const key = draftKey(userId, documentId);
    const store = useBenchmarkTakeoverStore;
    const recovered = readComparisonDraft(draftStorage, key);
    if (recovered && recovered.stage !== "closed" && !isDismissed(userId, documentId, recovered.sessionId) && store.getState().stage === "closed") {
      store.setState({ ...recovered, submitting: false, submitError: recovered.ratingClosedMessage || "Your comparison feedback was restored. You can continue where you left off." });
    }
    // Ratings/refunds survive the response being lost. Reconcile separately
    // from the draft so merely opening a document never submits or inserts prose.
    if (recovered) Auth.fetchWithIdToken("/api/benchmark/refunds/reconcile", {}).catch(() => {});
    const save = (state, previous) => {
      if (state.stage === "closed") {
        if (!previous?.sessionId) return;
        if (previous.failure && !previous.ratingClosedMessage) {
          // Closing the copy panel does not abandon a pair the server may
          // still recover. Keep its feedback without immediately reopening it.
          state = { ...previous, stage: "closed" };
        } else {
          if (!previous.ratingSubmitted) rememberDismissal(userId, documentId, previous.sessionId);
          setRecoverable(null);
          draftStorage.removeItem(key);
          return;
        }
      }
      try {
        if (state.ratingSubmitted) {
          draftStorage.removeItem(key);
        } else if ((state.generationDone || Object.values(state.sampleRevisions).some((revision) => revision > 0)) && (!state.failure || state.failure.preserveSamples) && !state.fallback) {
          const draft = Object.fromEntries(FIELDS.map((field) => [field, state[field]]));
          draftStorage.setItem(key, JSON.stringify({ version: VERSION, draft }));
        }
      } catch {
        // An unserializable draft must not interrupt the active comparison.
      }
    };
    const unsubscribe = store.subscribe(save);
    return () => {
      // Detach first: navigating away preserves the completed draft while
      // closing the global takeover keeps it out of another document/account.
      save(store.getState());
      unsubscribe();
      store.getState().close();
    };
  }, [userId, documentId]);

  useEffect(() => {
    if (userId && hasAccess) Auth.fetchWithIdToken("/api/benchmark/refunds/reconcile", {}).catch(() => {});
  }, [userId, hasAccess]);

  useEffect(() => {
    setRecoverable(null);
    if (!userId || !documentId || !hasAccess || (useBenchmarkTakeoverStore.getState().stage !== "closed" && !failedSessionId)) return undefined;
    let cancelled = false;
    const saved = readComparisonDraft(storage(), draftKey(userId, documentId));
    const savedSessionId = saved && !isDismissed(userId, documentId, saved.sessionId) ? saved.sessionId : undefined;
    Auth.fetchWithIdToken("/api/benchmark/sessions/recoverable", { documentId, sessionId: failedSessionId || savedSessionId }).then((response) => {
      const session = response?.session;
      if (cancelled || !session?.sessionId || isDismissed(userId, documentId, session.sessionId)) return;
      if (typeof session.samples?.A?.text !== "string" || typeof session.samples?.B?.text !== "string") return;
      const current = useBenchmarkTakeoverStore.getState();
      if (current.stage !== "closed" && (!current.failure || current.ratingClosedMessage || current.sessionId !== session.sessionId)) return;
      const savedLocal = readComparisonDraft(storage(), draftKey(userId, documentId));
      const local = savedLocal && !isDismissed(userId, documentId, savedLocal.sessionId) ? savedLocal : null;
      if (local && (local.ratingClosedMessage || local.sessionId !== session.sessionId)) return;
      if (local && ["A", "B"].some((sample) => (session.sampleRevisions?.[sample] || 0) < local.sampleRevisions[sample])) return;
      setRecoverable({ ...session, userId, documentId });
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [userId, documentId, hasAccess, failedSessionId]);

  const recoverableSession = recoverable?.userId === userId && recoverable?.documentId === documentId ? recoverable : null;
  const resumeComparison = () => {
    const state = useBenchmarkTakeoverStore.getState();
    const openFailure = state.failure && !state.ratingClosedMessage && state.sessionId === recoverableSession?.sessionId;
    if (!recoverableSession || (state.stage !== "closed" && !openFailure)) return;
    // Only an explicit click can replace a failed capture with the verified
    // full pair. Keep the latest feedback, including source-anchored tags.
    let local = openFailure ? Object.fromEntries(FIELDS.map((field) => [field, state[field]])) : readComparisonDraft(storage(), draftKey(userId, documentId));
    if (local?.sessionId !== recoverableSession.sessionId) local = null;
    if (local?.failure && !local.ratingClosedMessage && local.sessionId === recoverableSession.sessionId) {
      if (["A", "B"].some((sample) => (recoverableSession.sampleRevisions?.[sample] || 0) < (local.sampleRevisions?.[sample] || 0))) return;
      for (const sample of ["A", "B"]) {
        const revision = recoverableSession.sampleRevisions?.[sample] || 0;
        if (revision > (local.sampleRevisions?.[sample] || 0)) {
          local = { ...local, ...replacementSampleState(local, sample, revision) };
        }
      }
      const annotations = local.annotations.map((annotation) => {
        if (recoverableSession.samples[annotation.sample]?.text.startsWith(local.samples[annotation.sample]?.text || "")) return annotation;
        // A non-prefix replacement cannot safely reuse the old coordinates.
        // Retain the quoted feedback for the existing remove/retag review UI.
        return { ...annotation, start: -1, end: -1, sourceStart: null, sourceEnd: null };
      });
      state.beginSession({ sessionId: local.sessionId, surface: local.surface, precedingText: local.precedingText });
      useBenchmarkTakeoverStore.setState({ ...local, samples: recoverableSession.samples, annotations, sampleDone: { A: true, B: true }, generationDone: true, stage: "A", failure: null, submitting: false, submitError: null });
    } else if (local) {
      useBenchmarkTakeoverStore.setState({ ...local, submitting: false, submitError: local.ratingClosedMessage || "Your comparison feedback was restored. You can continue where you left off." });
    } else {
      state.beginSession({ sessionId: recoverableSession.sessionId, surface: recoverableSession.surface, precedingText: recoverableSession.precedingText || "" });
      useBenchmarkTakeoverStore.setState({ sampleRevisions: { A: 0, B: 0, ...recoverableSession.sampleRevisions } });
      state.appendSampleText("A", recoverableSession.samples.A.text);
      state.appendSampleText("B", recoverableSession.samples.B.text);
      state.markGenerationDone();
    }
    setRecoverable(null);
  };
  const dismissRecovery = () => {
    if (recoverableSession) rememberDismissal(userId, documentId, recoverableSession.sessionId);
    setRecoverable(null);
  };
  return { recoverableSession, resumeComparison, dismissRecovery };
}
